import React, { useState } from "react";
import Modal1 from "../modals/Modal1.js";
import PrimaryButton from "../buttons/PrimaryButton.js";
import SecondaryButton from "../buttons/SecondaryButton.js";
import Icon1 from "../icons/Icon.js";
import Space from "../layout/Space.js";
import { uploadToCarnet } from "../api/upload/uploadToCarnet.js";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken.js";
import { useAppContext } from "../config/AppContext.js";
import { useSnackbar } from "notistack";
import SmallSpinner from "../loading/SmallSpinner.js";

const REPERTORIES = [
  "Contrats d'assurance",
  "Contrats d'entretien",
  "Ouvrages exécutés",
  "Mes diagnostics",
  "Photos",
  "Factures",
  "Plans",
];

// Mapping from French repertory names to API doc types
const REPERTORY_TO_DOC_TYPE = {
  "Contrats d'assurance": "contrat",
  "Contrats d'entretien": "entretien",
  "Ouvrages exécutés": "ouvrage",
  "Mes diagnostics": "diagnostic",
  "Photos": "photos",
  "Factures": "factures",
  "Plans": "plans",
};

const DepotCILModal = ({ isOpen, onClose, folder }) => {
  const [selectedFiles, setSelectedFiles] = useState({});
  const [loading, setLoading] = useState(false);
  const { appState } = useAppContext();
  const { enqueueSnackbar } = useSnackbar();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const handleFileSelect = (repertory) => {
    const input = document.createElement("input");
    input.type = "file";
    input.multiple = true;

    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      if (files.length > 0) {
        setSelectedFiles((prev) => ({
          ...prev,
          [repertory]: [...(prev[repertory] || []), ...files],
        }));
      }
    };

    input.click();
  };

  const handleRemoveFile = (repertory, fileIndex) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [repertory]: prev[repertory].filter((_, index) => index !== fileIndex),
    }));
  };

  const handleSend = async () => {
    if (!folder || !folder.id) {
      enqueueSnackbar("Aucun dossier sélectionné", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const token = await getOrRefreshFirebaseToken();

      let uploadCount = 0;
      let errorCount = 0;

      // Upload files for each repertory
      for (const [repertory, files] of Object.entries(selectedFiles)) {
        const docType = REPERTORY_TO_DOC_TYPE[repertory];

        if (!docType) {
          console.error("Unknown repertory:", repertory);
          continue;
        }

        // Upload each file in this repertory
        for (const file of files) {
          try {
            await uploadToCarnet(folder.id, docType, file, token, prefix);
            uploadCount++;
          } catch (error) {
            console.error(`Error uploading ${file.name} to ${repertory}:`, error);
            errorCount++;
          }
        }
      }

      // Show success/error messages
      if (uploadCount > 0) {
        enqueueSnackbar(
          `${uploadCount} document${uploadCount > 1 ? "s" : ""} envoyé${uploadCount > 1 ? "s" : ""} avec succès`,
          { variant: "success" }
        );
      }

      if (errorCount > 0) {
        enqueueSnackbar(
          `${errorCount} document${errorCount > 1 ? "s" : ""} n'${errorCount > 1 ? "ont" : "a"} pas pu être envoyé${errorCount > 1 ? "s" : ""}`,
          { variant: "error" }
        );
      }

      if (uploadCount > 0) {
        handleClose();
      }
    } catch (error) {
      console.error("Error sending documents:", error);
      enqueueSnackbar("Erreur lors de l'envoi des documents", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedFiles({});
    onClose();
  };

  const getTotalFilesCount = () => {
    return Object.values(selectedFiles).reduce(
      (total, files) => total + (files?.length || 0),
      0
    );
  };

  return (
    <Modal1 isOpen={isOpen} onClose={handleClose}>
      <div className="bg-white rounded-xl w-fit max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex flex-row justify-between p-4 border-b-1">
          <div className="text-h2">Déposer dans le CIL</div>
          <div
            onClick={handleClose}
            className="cursor-pointer text-h3 text-shadow_50"
          >
            Fermer
          </div>
        </div>

        {/* Content */}
        <div className="flex flex-col gap-4 p-4 overflow-auto flex-1 w-c600">
          {REPERTORIES.map((repertory) => (
            <div
              key={repertory}
              className="border border-shadow_10 rounded-lg p-4"
            >
              <div className="flex flex-row justify-between items-center mb-3">
                <div className="text-h3 font-medium text-shadow_75">
                  {repertory}
                </div>
                <SecondaryButton
                  onClick={() => handleFileSelect(repertory)}
                  className="w-fit"
                >
                  <div className="flex flex-row items-center">
                    <Icon1 icon_name="add" size={16} fill="currentColor" />
                    <Space x={5} />
                    Ajouter
                  </div>
                </SecondaryButton>
              </div>

              {/* Selected files for this repertory */}
              {selectedFiles[repertory] && selectedFiles[repertory].length > 0 && (
                <div className="flex flex-col gap-2 mt-2">
                  {selectedFiles[repertory].map((file, index) => (
                    <div
                      key={index}
                      className="flex flex-row justify-between items-center bg-neutral-50 p-2 rounded"
                    >
                      <div className="flex flex-row items-center gap-2">
                        <Icon1 icon_name="file" size={16} fill="grey" />
                        <div className="text-h4 text-shadow_75">{file.name}</div>
                        <div className="text-h5 text-shadow_50">
                          ({Math.round(file.size / 1024)} KB)
                        </div>
                      </div>
                      <div
                        onClick={() => handleRemoveFile(repertory, index)}
                        className="cursor-pointer"
                      >
                        <Icon1
                          icon_name="close"
                          size={16}
                          fill="grey"
                          hover_color="red"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex flex-row justify-end gap-2 p-4 border-t-1">
          <SecondaryButton onClick={handleClose} disabled={loading}>
            Annuler
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSend}
            disabled={getTotalFilesCount() === 0 || loading}
          >
            <div className="flex flex-row items-center">
              {loading ? (
                <>
                  <SmallSpinner loading={true} />
                  <Space x={5} />
                  Envoi en cours...
                </>
              ) : (
                <>
                  Envoyer ({getTotalFilesCount()} document
                  {getTotalFilesCount() > 1 ? "s" : ""})
                </>
              )}
            </div>
          </PrimaryButton>
        </div>
      </div>
    </Modal1>
  );
};

export default DepotCILModal;
